// tslint:disable:no-commented-code
// tslint:disable:no-duplicate-string
// tslint:disable:only-arrow-functions
// tslint:disable:no-big-function

import {
  recommendationsPanelsLocaleKeys,
  spotlightBasketMarketingPanelHeading,
} from '@epicuro-next/constants/features/localization';
import { IFilterValue } from '@epicuro-next/data/reporting-api';
import {
  DateFilterConfig,
  SelectFilterConfig,
} from '@epicuro-next/features/dashboard';
import {
  IDynamicItem,
  IDynamicSimpleText,
} from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';
import { RecommendationsPanels } from '@epicuro-next/models/recommendation-panels';

import {
  getScrollableContainer,
  ScrollableContainerDataProviders,
} from '../../components/scrollable-container';
import {
  errorMessage,
  hasError,
  ifHasError,
  ifHasNoErrorAndIsNotLoading,
  ifIsLoading,
  isLoading,
  LoadingStateWrapper,
} from '../../loading-state-wrapper-helpers';
import { conditionalMessage } from '../../misc';
import {
  getFilterProperties,
  getSearchFiltersProvider,
  ProductBasketFilter,
} from '../../search-filters';

import {
  createInstance,
  createInstanceWithChildren,
  getProvider,
} from './../../../utils/dynamic-items-utils';
import { getDataForBestPerformingArticles } from './best-performing-acticles-helpers';

export function createHorizontalScrollablePanelWithBestPerformingArticles() {
  const root = createInstanceWithChildren({
    id: 'root',
    contentTypeId: 'dynamic-collections-renderer',
    data: {
      panelName: 'Basket',
      filterMissingImages: false,
    },
    styles: {
      classes: ['d-block', 'p-3'],
    },
    children: [],
  });

  const headerText: IDynamicSimpleText = {
    id: 'header-text',
    contentTypeId: 'dynamic-elements-simple-text',
    styles: {
      classes: ['font-weight-bold'],
      style: {
        'font-size': '18px',
      },
      variables: {
        '--text-dynamic-color': 'var(--dark-mode-gray-600, #414141)',
      },
    },
    inputs: {
      text: spotlightBasketMarketingPanelHeading,
    },
  };

  const bestPerformingArticlesFilters = createInstance({
    id: 'best-performing-articles-filter',
    contentTypeId: 'dynamic-elements-best-performing-articles-filters',
    if: ifHasNoErrorAndIsNotLoading(),
    styles: {
      classes: ['d-flex'],
    },
    data: {
      recommendationsPanelName: RecommendationsPanels.Spotlight,
      ...getFilterProperties(
        {
          provider: 'self',
          property: 'filtersChanged',
          startWith: { value: undefined },
        },
        {
          initialCategories: [],
          initialDaysIntoThePast: 30,
        },
      ),
    },
    inputs: {
      includeAllFilterValuesInFiltersChangedEvent: true,
      categoriesFilterConfig: {
        values: [
          {
            provider: 'parent',
            property: 'categories',
          },
          {
            provider: 'self',
            property: 'categoriesDefaultText',
          },
          {
            provider: 'self',
            property: 'showAllText',
          },
          {
            provider: 'self',
            property: 'hideFilterWhenNoValues',
          },
          {
            provider: 'self',
            property: 'initialCategories',
          },
        ],
        transform: function ([
          categories,
          categoriesDefaultText,
          showAllText,
          hideFilterWhenNoValues,
          initialCategories,
        ]: [IFilterValue[], string, string, boolean, string[]]):
          | SelectFilterConfig
          | undefined {
          if (hideFilterWhenNoValues && categories.length === 0) {
            return undefined; // This will cause the filter not to show at all.
          }

          return {
            values: categories,
            selected: initialCategories,
            defaultText: categoriesDefaultText,
            showAllText,
            isSingle: true,
          };
        }.toString(),
      },
      dateFilterConfig: {
        values: [
          {
            provider: 'self',
            property: 'daysIntoThePast',
          },
          {
            provider: 'self',
            property: 'initialPreselectedPeriod',
          },
        ],
        transform: function ([daysIntoThePast, initialPreselectedPeriod]: [
          number,
          { from: string; to: string },
        ]): DateFilterConfig {
          const now = new Date();
          const minDate = new Date();

          minDate.setDate(now.getDate() - daysIntoThePast);
          const config: DateFilterConfig = {
            minDate,
            maxDate: now,
          };

          config.preselected = initialPreselectedPeriod;

          return config;
        }.toString(),
      },
    },
  });

  const filtersLoadingState: IDynamicSimpleText = {
    id: 'filters-loading-state',
    contentTypeId: 'dynamic-elements-simple-text',
    if: ifIsLoading(),
    inputs: {
      text: recommendationsPanelsLocaleKeys.loadingFiltersPleaseWait,
    },
    styles: {
      classes: ['mr-3'],
      variables: {
        '--text-dynamic-color': 'var(--dark-mode-gray-600, #414141)',
      },
    },
  };

  const filterErrorState: IDynamicSimpleText = {
    id: 'filters-error-state',
    contentTypeId: 'dynamic-elements-simple-text',
    if: ifHasError(),
    inputs: {
      text: recommendationsPanelsLocaleKeys.anErrorOccurredWhileLoadingFilters,
    },
    styles: {
      classes: ['mr-3'],
      variables: {
        '--text-dynamic-color': 'var(--dark-mode-gray-600, #414141)',
      },
    },
  };

  const filterContent: IDynamicItem = {
    id: 'filters-content',
    contentTypeId: 'dynamic-collections-renderer',
    styles: {
      classes: ['d-flex', 'pt-1'],
    },
    children: [
      filtersLoadingState,
      filterErrorState,
      bestPerformingArticlesFilters,
    ],
  };

  const filterWrapper: IDynamicItem = {
    id: 'filter-wrapper',
    contentTypeId: 'dynamic-collections-renderer',
    data: {
      // SInce there is no network request anymore to get the filter values, but a path in the state is used
      // There is basically no loadingStateWrapper needed anymore.
      // This would ripple in such way that loading and error state dynamic elements would also need to be removed.
      // But if at somepoint it was decided to get it from an API endpoint, all this would be necessarya again.
      // Since the wrapper doesn't hurt, and it is here already, let's keep it just in case.
      loadingStateWrapper: {
        values: [getSearchFiltersProvider('categoryname')],
        wrapWithLoadingState: true,
        transform: function ([filters]: [ProductBasketFilter[]]) {
          if (filters.length === 0) {
            return [];
          }

          if (filters.length > 1) {
            const errorText = 'Multiple filters returned where 1 was expected.';
            console.error(errorText, filters);
            throw new Error(errorText);
          }

          const filterValues = filters[0].values;

          if (filterValues === undefined || filterValues === null) {
            return [];
          }

          const categories: IFilterValue[] = filterValues.map((filterValue) => {
            const fv: IFilterValue = {
              key: filterValue.displayName,
              value: filterValue.value,
            };

            return fv;
          });

          categories.sort((a, b) => a.key.localeCompare(b.key));

          return categories;
        }.toString(),
      },
      isLoading: isLoading(),
      hasError: hasError(),
      categories: {
        provider: 'self',
        property: 'loadingStateWrapper',
        transform: function (
          loadingStateWrapper: LoadingStateWrapper<IFilterValue[]>,
        ): IFilterValue[] {
          if (
            loadingStateWrapper.isLoading ||
            loadingStateWrapper.data === undefined
          ) {
            return [];
          }

          return loadingStateWrapper.data;
        }.toString(),
      },
    },
    children: [filterContent],
  };

  const header: IDynamicItem = {
    id: 'header',
    contentTypeId: 'dynamic-collections-renderer',
    styles: {
      classes: ['d-flex', 'flex-column'],
    },
    children: [headerText, filterWrapper],
  };

  const container = createInstance({
    id: 'container',
    contentTypeId: 'dynamic-collections-renderer',
    data: getDataForBestPerformingArticles(
      getProvider(root, 'panelName'),
      getProvider(bestPerformingArticlesFilters, 'filters'),
      {
        page: 'cb',
        location: 'de',
        type: 'hr',
        provider: 'nebp',
        cssVariant: '001',
      },
      getProvider(root, 'filterMissingImages'),
    ),
    children: [
      header,
      {
        contentTypeId: 'dynamic-collections-renderer',
        children: [
          errorMessage(),
          conditionalMessage(
            'no-items-message',
            'parent',
            'hasNoItems',
            recommendationsPanelsLocaleKeys.noRecommendedItemsReturned,
          ),
        ],
      },
    ],
  });

  root.children.push(container);

  // const eventHandlers = {
  //   onClick: [
  //     recommendationItemClickedEventHandler(
  //       getProvider(root, 'panelName'),
  //       getProvider(container, 'panelTrackingId'),
  //       { provider: 'parent', property: 'productData' },
  //     ),
  //     openConfigStripEventHandler('parent'),
  //   ],
  //   onVisibleOnce: [
  //     recommendationItemShownEventHandler(
  //       getProvider(root, 'panelName'),
  //       getProvider(container, 'panelTrackingId'),
  //       { provider: 'parent', property: 'productData' },
  //     ),
  //   ],
  // };

  const dataProviders: ScrollableContainerDataProviders = {
    isLoading: getProvider(container, 'isLoading'),
    hasError: getProvider(container, 'hasError'),
  };

  // const view = getProductAsLoadableImageView(eventHandlers);

  const horizontalScrollableContainer = getScrollableContainer(dataProviders);

  const scrollableContainer = horizontalScrollableContainer.scrollableContainer;
  if (scrollableContainer.styles === undefined) {
    scrollableContainer.styles = {};
  }
  scrollableContainer.styles.classes ??= [];
  scrollableContainer.styles.classes.push('d-flex', 'mt-n2');
  container.children.push(scrollableContainer);

  return {
    root,
  };
}
